import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * The last line before a white screen.
 *
 * A homeowner who hits a bug mid-session still has a booked visit and a real person to talk
 * to; saying so is more useful than a stack trace. The details go to the console for the
 * device-lab runs, never to the screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[asb] render error', error, info.componentStack)
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children

    return (
      <main
        style={{
          maxWidth: '32rem',
          margin: '4rem auto',
          padding: '0 1.5rem',
          fontFamily: 'var(--font)',
        }}
      >
        <p className="eyebrow">Something broke</p>
        <h1 style={{ margin: '0.75rem 0 1rem' }}>Sorry — this screen stopped working.</h1>
        <p style={{ margin: '0 0 1rem' }}>
          Your estimate and any visit you have booked are safe. Reloading usually fixes it.
        </p>
        <p style={{ margin: '0 0 1.5rem' }}>
          Still stuck? Email{' '}
          <a href="mailto:andrew@asbhomerenovations.com" style={{ color: 'var(--blue-deep)' }}>
            andrew@asbhomerenovations.com
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            background: 'var(--blue)',
            color: '#fff',
            border: 0,
            borderRadius: 'var(--r-btn)',
            padding: '0.85rem 1.5rem',
            font: 'inherit',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </main>
    )
  }
}
